import React from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';

// ── "You can look around, but here is what we still need" ──────────────────
//
// The portal used to be a locked door. A technician who signed up and did not
// finish the application got the application, and nothing else — no jobs, no
// estimates, no profile, no sense of what he had joined. Which is backwards:
// the reason to finish the form is to get at the thing behind it, and he could
// not see the thing behind it.
//
// So the door is open and this is the note on the wall. It sits on every
// screen until there is nothing left to chase, and it is specific: not
// "complete your profile" but the actual two things missing, in the order they
// matter, with one button that goes to the right place.
//
// It is honest about the consequence. A technician reading "almost there!"
// while quietly not being sent any work would rightly never trust the app
// again. Work and payment both wait on this, and it says so.

export default function VendorSetupNote() {
  const { vendorProfile, setVendorView } = useApp();
  const { t } = useI18n();
  if (!vendorProfile) return null;

  const profile = vendorProfile.profile || {};
  const compliance = vendorProfile.compliance || {};

  // The two gates, and they are genuinely different. The application is what
  // tells QPS what this person does and where. The paperwork is what makes it
  // legal to pay them. Neither substitutes for the other.
  const applicationDone = Boolean(profile.completedAt);
  const paperwork = (compliance.items || []).filter((i) => !i.optional);
  const missingPaper = paperwork.filter((i) => i.status === 'missing' || i.status === 'expired');

  if (applicationDone && missingPaper.length === 0) return null;

  // What to say, and where the button goes. Application first: without it
  // nobody knows what trades to send them, so chasing a W-9 before it is
  // chasing the wrong thing.
  const step = !applicationDone
    ? {
      heading: t('vsn.finishTitle', 'Finish setting up your account'),
      body: t('vsn.finishBody', 'Tell us what you do and where you work. It takes a couple of minutes and you can stop and come back.'),
      cta: t('vsn.finishCta', 'Finish my details'),
      go: () => setVendorView('apply'),
    }
    : missingPaper.length === 1
      ? {
        heading: `${t('vsn.needOne', 'We still need your')} ${label(missingPaper[0])}`,
        body: t('vsn.photoOne', 'A photo of it from your phone is fine. Nothing has to be printed or scanned.'),
        cta: t('vsn.addOne', 'Add it now'),
        go: () => setVendorView('docs'),
      }
      : {
        heading: `${t('vsn.needMany', 'We still need these from you')}`,
        body: `${sentenceCase(joinList(missingPaper.map(label)))}. ${t('vsn.photoMany', 'A photo of each from your phone is fine.')}`,
        cta: t('vsn.addMany', 'Add my documents'),
        go: () => setVendorView('docs'),
      };

  return (
    <div className="vsn">
      <div className="vsn-head">{step.heading}</div>
      <p className="vsn-body">{step.body}</p>

      {/* The line this note existed without for a day, and the reason a
          technician who was already working sat on his invoices.
          "We cannot pay you until this is done" is true and was all it said,
          and a man reads that as "do not bother sending anything". He should
          send them. They sit and get paid the day the paperwork lands. */}
      <p className="vsn-ok">
        {t('vsn.stillSend', 'Keep sending your estimates and invoices as normal. They are paid as soon as this is in.')}
      </p>
      <p className="vsn-why">
        {t('vsn.thenWork', 'New jobs start coming to you once it is on file.')}
      </p>

      <button type="button" className="vsn-btn" onClick={step.go}>{step.cta}</button>
    </div>
  );
}

// The document's name as a person says it, not as the database stores it.
function label(item) {
  const NAMES = {
    w9: 'W-9',
    insurance: 'proof of insurance',
    'business-license': 'business licence',
    'occupational-license': 'occupational licence',
  };
  return NAMES[item.type] || item.label || item.type;
}

const joinList = (parts) => (parts.length > 1
  ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  : parts[0] || '');

// "W-9" must keep its capital; "proof of insurance" must gain one when it
// starts the sentence.
const sentenceCase = (str) => (str ? str[0].toUpperCase() + str.slice(1) : str);
